import { useState, useEffect } from 'react';
import {
  Button,
  Badge,
  Input,
  Label,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Spinner,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@rnd-payplus/alloy-react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { FilterButton, SearchFilterButton } from '../components/FilterButton';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { usePageShell } from '../contexts/PageShellContext';

interface Tenant {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'inactive';
  createdAt: string;
  ownerEmail: string;
}

const TenantsPage = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>([]);
  const [, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [nameFilter, setNameFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    ownerEmail: '',
    description: '',
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    let filtered = tenants;
    if (statusFilter.length > 0) {
      filtered = filtered.filter((t) => statusFilter.includes(t.status));
    }
    if (nameFilter) {
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(nameFilter.toLowerCase()) ||
          t.ownerEmail.toLowerCase().includes(nameFilter.toLowerCase()),
      );
    }
    if (regionFilter.length > 0) {
      // Region filtering when data includes region field
      filtered = filtered.filter((t) => regionFilter.includes((t as any).region));
    }
    setFilteredTenants(filtered);
    setPage(0);
  }, [statusFilter, nameFilter, regionFilter, tenants]);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/tenants');
      if (!response.ok) {
        throw new Error(`Failed to load tenants (${response.status})`);
      }
      setTenants(await response.json());
    } catch (err) {
      setTenants([]);
      toast.error(err instanceof Error ? err.message : 'Failed to load tenants');
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.ownerEmail) {
      toast.error('Name and Owner Email are required');
      return;
    }

    try {
      const response = await fetch('/api/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          ownerEmail: formData.ownerEmail,
          description: formData.description,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create tenant (${response.status})`);
      }

      toast.success('Tenant created successfully!');
      setDialogOpen(false);
      setFormData({ name: '', ownerEmail: '', description: '' });
      fetchTenants();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create tenant');
    }
  };

  const handleEdit = async () => {
    if (!selectedTenant) return;

    try {
      const response = await fetch(`/api/tenants/${selectedTenant.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update tenant (${response.status})`);
      }

      toast.success('Tenant updated!');
      setEditDialogOpen(false);
      fetchTenants();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update tenant');
    }
  };

  const handleDelete = async () => {
    if (!selectedTenant) return;

    try {
      const response = await fetch(`/api/tenants/${selectedTenant.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete tenant (${response.status})`);
      }

      toast.success('Tenant deleted!');
      setDeleteDialogOpen(false);
      setTenants(tenants.filter(t => t.id !== selectedTenant.id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete tenant');
    }
  };

  const getStatusVariant = (status: string): 'default' | 'destructive' | 'secondary' => {
    switch (status) {
      case 'active': return 'default';
      case 'suspended': return 'secondary';
      case 'inactive': return 'destructive';
      default: return 'secondary';
    }
  };

  const total = tenants.length;
  const active = tenants.filter(t => t.status === 'active').length;
  const suspended = tenants.filter(t => t.status === 'suspended').length;
  const inactive = tenants.filter(t => t.status === 'inactive').length;

  const paginatedTenants = filteredTenants.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredTenants.length / rowsPerPage);

  usePageShell({
    title: 'Tenant Management',
    primaryAction: (
      <Button onClick={() => setDialogOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Create Tenant
      </Button>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Tenants' },
    ],
  });

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Total" value={total} subtitle="Tenants" />
        <KPICard title="Active" value={active} subtitle="Tenants" />
        <KPICard title="Suspended" value={suspended} subtitle="Tenants" />
        <KPICard title="Inactive" value={inactive} subtitle="Tenants" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <FilterButton
          label="Status"
          options={['active', 'suspended', 'inactive']}
          selected={statusFilter}
          onChange={setStatusFilter}
        />
        <SearchFilterButton
          label="Name"
          value={nameFilter}
          onChange={setNameFilter}
        />
        <FilterButton
          label="Region"
          options={[...new Set(tenants.map((t) => (t as any).region).filter(Boolean))]}
          selected={regionFilter}
          onChange={setRegionFilter}
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Owner Email</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedTenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No tenants found.
                </TableCell>
              </TableRow>
            ) : (
              paginatedTenants.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell className="font-mono text-sm">{tenant.id}</TableCell>
                  <TableCell>{tenant.name}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(tenant.status)}>
                      {tenant.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{tenant.ownerEmail}</TableCell>
                  <TableCell>{tenant.createdAt}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedTenant(tenant);
                        setFormData({ name: tenant.name, ownerEmail: tenant.ownerEmail, description: '' });
                        setEditDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => {
                        setSelectedTenant(tenant);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          <Select
            value={String(rowsPerPage)}
            onValueChange={(value) => {
              setRowsPerPage(parseInt(value, 10));
              setPage(0);
            }}
          >
            <SelectTrigger className="w-[70px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {filteredTenants.length === 0
              ? '0 of 0'
              : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, filteredTenants.length)} of ${filteredTenants.length}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
            <DialogDescription>
              Add a new tenant to the platform.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Tenant Name *</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter tenant name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Owner Email *</Label>
              <Input
                id="create-email"
                type="email"
                value={formData.ownerEmail}
                onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
                placeholder="admin@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-desc">Description</Label>
              <textarea
                id="create-desc"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Tenant</DialogTitle>
            <DialogDescription>
              Update the tenant details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Tenant Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Owner Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.ownerEmail}
                onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Tenant</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedTenant?.name}"? This will remove all data and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantsPage;
